import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ConsultancyCard, ConsultancyService, Supervisor } from '../../dashboard/services/consultancy.service';
import { AuthService } from '../../auth/services/auth.service';
import { ConversationService } from '../../shared/services/conversation.service';
import { ModalService } from '../../shared/services/modal.service';
import { catchError, of, retry } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { BreadcrumbComponent, BreadcrumbItem } from '../../components/common/breadcrumb/breadcrumb.component';
import { ContentLoaderComponent } from '../../components/common/content-loader/content-loader.component';
import { PaginationComponent } from '../../components/common/pagination/pagination.component';
import { ConsultancyCardComponent } from '../../components/consultancy/consultancy-card/consultancy-card.component';
import { ButtonComponent } from '../../components/forms/button/button.component';
import { FiltersDropdownComponent } from '../../components/common/filters-dropdown/filters-dropdown.component';
import { CheckboxComponent } from '../../components/form/checkbox/checkbox.component';
import { ContentErrorComponent } from '../../components/common/content-error/content-error.component';
import { AuthModalComponent } from '../../auth/components/auth-modal/auth-modal.component';
import { ListingsSearchboxComponent } from '../../components/common/listings-searchbox/listings-searchbox.component';
import { SeoService } from '../../shared/services/seo.service';

@Component({
  selector: 'app-consultancy',
  standalone: true,
  imports: [CommonModule, AuthModalComponent, FormsModule, BreadcrumbComponent, ContentLoaderComponent, PaginationComponent, ConsultancyCardComponent, ButtonComponent, FiltersDropdownComponent, CheckboxComponent, ContentErrorComponent, ListingsSearchboxComponent],
  templateUrl: './consultancy.component.html',
  styleUrl: './consultancy.component.css'
})
export class ConsultancyComponent implements OnInit, OnDestroy {
  breadcrumbItems: BreadcrumbItem[] = [
    { label: '', routerLink: '/home', icon: 'fas fa-home' },
    { label: 'Consultancies', routerLink: '/consultancy', disabled: true }
  ];

  filters = {
    searchBy: '1',
    price: '1'
  }
  consultancies: ConsultancyCard[] = [];
  filteredConsultancies: ConsultancyCard[] = [];
  isLoading = true;
  error: string | null = null;
  retryCount = 0;
  maxRetries = 3;

  // Search properties
  searchTerm: string = '';
  sortOrder: 'desc' | 'asc' = 'desc';
  priceRange = { min: 0, max: 100000, single: true, project: true };
  priceCheck = { single: true, project: true };

  page = 1;
  limit = 12;
  totalItems = 0;


  supervisors: Supervisor[] = [];

  onPageChange(e: any) {
    this.page = e;
    this.loadApprovedConsultancies();
  }
  // Login modal properties
  showLoginModal = false;
  pendingChatRequest: {
    consultancy: ConsultancyCard;
    teacherId: string;
    teacherName: string;
  } | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private consultancyService: ConsultancyService,
    private router: Router,
    private authService: AuthService,
    private conversationService: ConversationService,
    private modalService: ModalService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private seoService: SeoService
  ) { }

  generateConsultanciesSchema() {
    return {
      "@context": "https://schema.org",
      "@graph": [

        {
          "@type": "CollectionPage",
          "name": "Research Consultancy Services",
          "description": "Browse consultancy services offered by expert eSupervisors.",
          "url": `https://researchdecode.com/consultancies?page=${this.page}`,
          "mainEntity": {
            "@type": "ItemList"
          },
          "hasPart": {
            "@type": "ItemList",
            "name": "Consultancy Listings"
          },
          "isPartOf": {
            "@type": "WebSite",
            "name": "Research Decode"
          }
        },

        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://researchdecode.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Consultancies",
              "item": "https://researchdecode.com/consultancies"
            }
          ]
        },

        {
          "@type": "ItemList",
          "name": "Consultancy Listings",
          "itemListOrder": "http://schema.org/ItemListOrderDescending",
          "numberOfItems": this.consultancies.length,
          "itemListElement": this.consultancies.map((item, index) => ({
            "@type": "ListItem",
            "position": ((this.page - 1) * 12) + index + 1,
            "url": `https://researchdecode.com/consultancies/${item.slug}`,
            "name": item.title
          }))
        }

      ]
    };
  }

  ngOnInit(): void {

    this.route.queryParams.subscribe(params => {
      if (params['search']) {
        this.searchTerm = params['search'];
        // this.loadApprovedConsultancies();
      }
      if (params['page']) {
        this.page = Number(params['page']) || 1
      }

      this.seoService.setMeta({
        title: 'Research Consultancy Services | Hire Academic Experts',
        description: 'Browse research consultancy services, thesis help, and academic guidance from expert eSupervisors. Filter by category, price, and connect with professionals.',
        url: 'https://researchdecode.com/consultancies',
        noIndex: this.searchTerm ? true : false
      });
    });
    this.loadApprovedConsultancies(this.searchTerm);

    this.setupModalSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeOrder(order: 'asc' | 'desc') {
    this.sortOrder = order;
    this.loadApprovedConsultancies(this.searchTerm);
  }

  /**
   * Setup modal service subscriptions
   */
  private setupModalSubscriptions(): void {
    this.modalService.modalClose$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.closeLoginModal();
    });
  }

  /**
   * Enhanced method to load approved consultancies with retry logic
   */
  loadApprovedConsultancies(search: string = ''): void {
    this.isLoading = true;
    this.error = null;

    const skip = (this.page - 1) * this.limit;

    this.consultancyService.getApprovedConsultancies({ limit: this.limit, skip: skip, search, sortOrder: this.sortOrder, priceRange: this.priceRange })
      .pipe(
        takeUntil(this.destroy$),
        retry(2),
        catchError((error) => {
          console.error('❌ Error loading consultancies:', error);
          this.error = 'Failed to load consultancy services. Showing fallback data.';
          return of({ data: [], success: false });
        })
      )
      .subscribe({
        next: (response: any) => {
          console.log('✅ Approved consultancies loaded:', response);

          // Handle both single object and array responses
          if (response.data) {
            if (Array.isArray(response.data) && response.data.length > 0) {
              this.consultancies = response.data;
              this.filteredConsultancies = response.data;
              const schema = this.generateConsultanciesSchema();
              this.seoService.setSchema(schema);
              this.totalItems = response.count || 0;
              this.error = null;
            } else if (!Array.isArray(response.data)) {
              // Single object response - convert to array
              this.consultancies = [response.data];
              this.filteredConsultancies = [response.data];
              this.error = null;
            } else {
              // Empty array
              this.consultancies = [];
              this.filteredConsultancies = [];
              this.error = null;
            }
          } else {
            this.consultancies = [];
            this.filteredConsultancies = [];
            this.error = null;
          }

          this.isLoading = false;
        },
        error: (error) => {
          console.error('❌ Final error after retries:', error);
          this.error = 'Failed to load consultancy services. Please try again later.';
          this.isLoading = false;
          this.consultancies = [];
        }
      });
  }

  /**
   * Retry loading consultancies with exponential backoff
   */
  retryLoading(): void {
    this.retryCount++;
    console.log(`Retrying to load consultancies (attempt ${this.retryCount})`);

    setTimeout(() => {
      this.loadApprovedConsultancies();
    }, 1000);
  }

  /**
   * Performance optimization - trackBy function
   */
  trackByConsultancyId(index: number, consultancy: ConsultancyCard): string {
    return consultancy._id || consultancy.id || index.toString();
  }

  /**
   * TrackBy function for static supervisors
   */
  trackBySupervisorId(index: number, supervisor: any): number {
    return supervisor.id || index;
  }

  /**
   * Enhanced method to get display name with fallback
   */
  getTeacherDisplayName(consultancy: ConsultancyCard): string {
    if (consultancy.teacher?.fullName) {
      return consultancy.teacher.fullName;
    }

    if (consultancy.teacher?.firstName && consultancy.teacher?.lastName) {
      return `${consultancy.teacher.firstName} ${consultancy.teacher.lastName}`;
    }

    if (consultancy.teacherId?.firstName && consultancy.teacherId?.lastName) {
      return `${consultancy.teacherId.firstName} ${consultancy.teacherId.lastName}`;
    }

    return 'Expert Consultant';
  }

  /**
   * Enhanced method to get teacher image with multiple fallbacks
   */
  getTeacherImage(consultancy: ConsultancyCard): string {
    // First try to get actual profile picture
    const image = consultancy?.imagePath;

    // If no image found, use gender-based placeholder
    if (!image) {
      const gender = consultancy?.teacher?.gender;

      // Use female placeholder for female, male placeholder for others/default
      if (gender === 'female') {
        return 'assets/img/cardImg.png';
      } else {
        // Default to male placeholder for male, other, prefer-not-to-say, or null
        return 'assets/img/imgae_eizrwi.png';
      }
    }

    return image;
  }

  /**
   * Enhanced method to get display price with better formatting
   */
  getConsultancyPrice(consultancy: ConsultancyCard): string {
    const singlePrice = consultancy.pricing?.single;
    if (singlePrice) {
      const numericPrice = parseFloat(singlePrice);
      if (!isNaN(numericPrice)) {
        return `₹${numericPrice.toLocaleString()}/session`;
      }
      return `₹${singlePrice}/session`;
    }
    return 'Price on request';
  }

  /**
   * Enhanced method to get teacher rating with fallback
   */
  getTeacherRating(consultancy: ConsultancyCard): number {
    return consultancy.teacher?.rating ||
      consultancy.teacherId?.rating ||
      4.5;
  }

  /**
   * Enhanced method to get teacher specialization with fallback
   */
  getTeacherSpecialization(consultancy: ConsultancyCard): string {
    return consultancy.teacher?.specialization ||
      consultancy.teacherId?.specialization ||
      'Expert Consultation';
  }

  /**
   * New method to get teacher experience
   */
  getTeacherExperience(consultancy: ConsultancyCard): string {
    const experience = consultancy.teacher?.experience || consultancy.teacherId?.experience;
    return experience || 'Experienced Professional';
  }

  /**
   * New method to check if teacher data is available
   */
  hasValidTeacherData(consultancy: ConsultancyCard): boolean {
    return !!(consultancy.teacher?.firstName && consultancy.teacher?.lastName) ||
      !!(consultancy.teacherId?.firstName && consultancy.teacherId?.lastName);
  }

  /**
   * Handle hiring a consultant - navigate to detail page
   */
  hireConsultant(consultancy: ConsultancyCard): void {
    console.log('Navigating to consultant detail:', {
      consultancyId: consultancy._id || consultancy.id,
      title: consultancy.title,
      teacher: this.getTeacherDisplayName(consultancy)
    });

    // Get the teacher ID from the consultancy
    const teacherId = this.getTeacherId(consultancy);
    const consultancyId = consultancy._id || consultancy.id;

    if (teacherId && consultancyId) {
      // Navigate with both teacherId and consultancyId
      this.router.navigate(['/consultancies', consultancy?.slug]);
    } else if (teacherId) {
      // Fallback to teacher-only route
      this.router.navigate(['/consultancies', consultancy?.slug]);
    } else {
      console.error('Teacher ID not found for consultancy:', consultancy);
      // You might want to show a toast notification here
    }
  }

  /**
   * Legacy method for static supervisors (keeping for backward compatibility)
   */
  hireSupervisor(supervisorId: number): void {
    console.log('Hiring supervisor with ID:', supervisorId);
    // Navigate to consultancy detail with the supervisor ID
    // Note: In a real scenario, you'd map supervisor IDs to actual teacher IDs from your database
    this.router.navigate(['/esupervisors', supervisorId]);
  }

  /**
   * Check if we should show fallback static data
   */
  shouldShowFallbackData(): boolean {
    return !this.isLoading && this.consultancies.length === 0 && !this.error;
  }

  /**
   * Enhanced method to handle chatting with a teacher using modal
   */
  chatWithTeacher(consultancy: ConsultancyCard): void {
    console.log('🎯 Chat with teacher requested:', {
      consultancy: {
        id: consultancy._id || consultancy.id,
        title: consultancy.title,
        teacher: consultancy.teacher,
        teacherId: consultancy.teacherId
      }
    });

    const teacherId = this.getTeacherId(consultancy);
    const teacherName = this.getTeacherDisplayName(consultancy);

    if (!teacherId) {
      console.error('❌ Teacher ID not found:', consultancy);
      this.toastr.error('Teacher information not available', 'Error');
      return;
    }

    if (!teacherName || teacherName === 'Expert Consultant') {
      console.warn('⚠️ Teacher name not found, using fallback');
    }

    // Check if user is authenticated
    if (!this.authService.isAuthenticated()) {
      console.log('🔒 User not authenticated, showing login modal');

      // Store the pending chat request
      this.pendingChatRequest = {
        consultancy,
        teacherId,
        teacherName
      };

      // Show login modal
      this.showLoginModal = true;
      return;
    }

    // Check if current user is an expert
    // if (this.authService.isExpert()) {
    //   this.toastr.info('To chat with an expert, Please Login with the Student Account', 'Student Account Required');
    //   return;
    // }

    // User is authenticated and not an expert, proceed with chat
    this.proceedWithChat(consultancy, teacherId, teacherName);
  }

  /**
   * Proceed with chat after authentication
   */
  private proceedWithChat(consultancy: ConsultancyCard, teacherId: string, teacherName: string): void {
    console.log('Proceeding with chat for teacher:', { teacherId, teacherName });

    // Check if conversation already exists
    const existingConversation = this.conversationService.findConversationByTeacher(teacherId);

    if (existingConversation) {
      // Navigate to existing conversation
      this.navigateToChat(teacherId, teacherName, consultancy.title, existingConversation._id, consultancy._id);
    } else {
      // Initiate new conversation
      this.initiateNewConversation(consultancy, teacherId, teacherName);
    }
  }

  /**
   * Initiate a new conversation with the teacher
   */
  private initiateNewConversation(consultancy: ConsultancyCard, teacherId: string, teacherName: string): void {
    console.log('🚀 Initiating new conversation:', {
      teacherId,
      teacherName,
      consultancy: {
        id: consultancy._id || consultancy.id,
        title: consultancy.title
      }
    });

    const consultancyData = {
      consultancyId: consultancy._id || consultancy.id || '',
      title: consultancy.title,
      description: consultancy.description || ''
    };

    this.conversationService.initiateConversation(teacherId, consultancyData, undefined, 'consultancy')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('✅ Conversation response:', response);

          if (response.data) {
            const conversation = Array.isArray(response.data) ? response.data[0] : response.data;



            // Navigate to chat page
            this.navigateToChat(teacherId, teacherName, consultancy.title, conversation._id, consultancy?._id);
          } else {
            console.error('❌ Conversation initiation failed:', response);
            this.toastr.error(response?.error?.msg || response.message || 'Failed to start conversation', 'Error');
          }
        },
        error: (error) => {
          console.error('❌ Error initiating conversation:', error);

          let errorMessage = 'Failed to start conversation. Please try again.';

          if (error.error?.message) {
            errorMessage = error.error.message || error?.error?.msg;
          } else if (error.status === 401) {
            errorMessage = 'Authentication required. Please login again.';
            this.authService.logout();
            this.showLoginModal = true;
            return;
          } else if (error.status === 404) {
            errorMessage = 'Teacher not found or not available for chat.';
            console.warn('⚠️ Backend API endpoint not available. Using fallback system.');
          } else if (error.status === 500) {
            errorMessage = error?.error?.msg || 'Server error. Please try again later.';
          }

          this.toastr.error(errorMessage, 'Error');
        }
      });
  }

  /**
   * Navigate to chat page with context
   */
  // private navigateToChat(teacherId: string, teacherName: string, consultancyTitle: string, conversationId?: string): void {
  //   const queryParams: any = {
  //     teacherId,
  //     teacherName,
  //     consultancyTitle
  //   };

  //   if (conversationId) {
  //     queryParams.conversationId = conversationId;
  //   }

  //   this.router.navigate(['/user-dashboard/chat'], {
  //     queryParams
  //   });
  // }
  private navigateToChat(teacherId: string, teacherName: string, consultancyTitle: string, conversationId?: string, consultancyId?: string): void {
    // Determine chat route based on user type
    const chatRoute = this.authService.isExpert() ? '/dashboard/chat' : '/user-dashboard/chat';

    this.router.navigate([chatRoute], {
      queryParams: {
        teacherId: teacherId,
        teacherName: teacherName,
        consultancyTitle: consultancyTitle,
        consultancyId: consultancyId || consultancyId || '',
        conversationId: conversationId
      }
    });
  }

  /**
   * Handle successful login from modal
   */
  onLoginSuccess(): void {
    this.toastr.success('Login successful!', 'Welcome!');
    this.closeLoginModal();

    // If there was a pending chat request, proceed with it
    if (this.pendingChatRequest) {
      const { consultancy, teacherId, teacherName } = this.pendingChatRequest;

      // Small delay to ensure auth state is updated
      setTimeout(() => {
        this.proceedWithChat(consultancy, teacherId, teacherName);
      }, 500);
    }
  }

  /**
   * Close login modal and clear pending request
   */
  closeLoginModal(): void {
    this.showLoginModal = false;
    this.pendingChatRequest = null;
  }

  /**
   * Helper method to get teacher ID from consultancy
   */
  private getTeacherId(consultancy: ConsultancyCard): string | null {


    // Try different ways to get teacher ID
    let teacherId = null;

    // Method 1: Direct teacherId object with _id
    if (consultancy.teacherId && typeof consultancy.teacherId === 'object' && consultancy.teacherId._id) {
      teacherId = consultancy.teacherId._id;
    }
    // Method 2: Direct teacher object with _id
    else if (consultancy.teacher && typeof consultancy.teacher === 'object' && consultancy.teacher._id) {
      teacherId = consultancy.teacher._id;
    }
    // Method 3: teacherId as string
    else if (consultancy.teacherId && typeof consultancy.teacherId === 'string') {
      teacherId = consultancy.teacherId;
    }
    // Method 4: teacher as string (fallback)
    else if (consultancy.teacher && typeof consultancy.teacher === 'string') {
      teacherId = consultancy.teacher;
    }

    if (!teacherId) {
      console.error('❌ Could not extract teacher ID from consultancy:', consultancy);
    }

    return teacherId;
  }

  getShortDescription(consultancy: ConsultancyCard): string {
    return consultancy?.description?.slice(0, 50);
  }

  /**
   * Filter consultancies based on search term
   */
  applyFilters(): void {
    this.loadApprovedConsultancies(this.searchTerm);
  }

  /**
   * Reset all filters
   */
  resetFilters(): void {
    this.searchTerm = '';
    this.loadApprovedConsultancies(this.searchTerm);
  }

}


